// How tall a widget's frame is, from what the widget reports through
// `widget.resize`. The host half of sizing: the widget SDK's own `useAutoHeight`
// measures inside the frame and sends the number, and this decides what the host
// does with it.

import type { WidgetKind } from '@luminovo/apps-types';
import { useCallback, useRef, useState } from 'react';

import { EMBEDDED_WIDGET_KINDS } from './useSlotEmbeds';

// How tall a slot is while nobody has told it otherwise.
//
// An iframe has no intrinsic height, so SOME number has to be picked here, and every
// number is wrong for something: this one leaves a short widget floating in dead
// space and puts a tall one behind an inner scrollbar. It stays as the height for a
// frame that is still loading, and as the permanent height for a widget built
// against an SDK older than `widget.resize` — which is why it is not being lowered.
export const UNSIZED_SLOT_HEIGHT_PX = 400;

// The tallest a `container` or `tab` frame grows. Both sit inside a product page
// that has its own content around it, so past this the widget's content scrolls
// inside the frame rather than pushing the page's own content out of reach. A
// full-page frame has the page to itself and follows its widget all the way.
export const CONTAINER_MAX_HEIGHT_PX = 1200;

export interface FrameHeight {
    /** Record a height the widget reported. Rounded up: a fractional height truncated is a one-pixel scrollbar. */
    report: (height: number) => void;
    /** The height applied to the frame as a px string, or undefined while nothing applies yet. */
    applied: string | undefined;
    /** The `height` and `minHeight` of the box around the frame. */
    box: { height: string; minHeight: string };
    /** The frame's own `minHeight`. */
    frameMinHeight: string | number;
}

export interface UseFrameHeightOptions {
    kind: WidgetKind;
    /**
     * The frame instance the reports belong to. A new document makes no claim
     * about its height, and the old one's claim was about a page that is gone, so a
     * report for another instance is ignored.
     */
    instanceKey: string;
    /**
     * Whether the widget is rendering. A widget reports as soon as it has laid
     * anything out, which includes the "Loading…" line it shows while waiting for
     * the slot context; honouring that would collapse the box around the spinner the
     * host draws over it, and again on every session recovery.
     */
    ready: boolean;
}

export function useFrameHeight({ kind, instanceKey, ready }: UseFrameHeightOptions): FrameHeight {
    const [reported, setReported] = useState<{ instance: string; height: number }>();
    // Read through a ref so `report` keeps one identity: the message listener that
    // calls it would otherwise be re-bound on every new frame instance.
    const instanceRef = useRef(instanceKey);
    instanceRef.current = instanceKey;

    // The last report taken, so a repeat is dropped before it reaches React: a
    // widget reports on every layout change, mostly with the same number, and a
    // state updater that returns the old value still costs a render.
    const lastReportRef = useRef(reported);

    const report = useCallback((height: number) => {
        const next = { instance: instanceRef.current, height: Math.ceil(height) };
        const last = lastReportRef.current;
        if (last?.instance === next.instance && last.height === next.height) return;
        lastReportRef.current = next;
        setReported(next);
    }, []);

    const contentHeight = reported?.instance === instanceKey ? reported.height : undefined;
    const capped =
        contentHeight === undefined || !EMBEDDED_WIDGET_KINDS.has(kind)
            ? contentHeight
            : Math.min(contentHeight, CONTAINER_MAX_HEIGHT_PX);
    // Written as a px string, not a number: MUI's sizing transform reads a number <= 1
    // as a percentage, so a widget reporting a single pixel would otherwise get 100%.
    const applied = ready && capped !== undefined ? `${capped}px` : undefined;

    return {
        report,
        applied,
        box: {
            // A full-page frame takes as much room as the page gives while nobody has
            // said how much is wanted. A container sits in a column of the page's own
            // content, where `100%` means nothing useful, so it starts at the floor.
            // A measured height replaces both the guess and its floor, or the floor
            // would be the dead space.
            height: applied ?? (EMBEDDED_WIDGET_KINDS.has(kind) ? `${UNSIZED_SLOT_HEIGHT_PX}px` : '100%'),
            minHeight: applied ?? `${UNSIZED_SLOT_HEIGHT_PX}px`,
        },
        // Follows the box rather than repeating its floor: a floor here would win over
        // a measured height and put the scrollbar back inside a widget that had just
        // told us how to avoid it.
        frameMinHeight: applied ?? UNSIZED_SLOT_HEIGHT_PX,
    };
}
